import React, { useState, forwardRef, useEffect } from 'react';
import { ChevronDown, AlertTriangle, LayoutGrid, AlertCircle, TrendingUp, Calendar, Check, FileText } from 'lucide-react';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const CustomDateInput = forwardRef(({ value, onClick }, ref) => (
    <button
        className="flex items-center justify-between px-4 py-2 bg-white border border-gray-200 rounded-lg shadow-sm text-gray-900 font-bold text-base min-w-[220px] hover:bg-gray-50 transition-colors"
        onClick={onClick}
        ref={ref}
    >
        <span className="flex items-center gap-2">
            <Calendar size={18} className="text-gray-500" />
            {value}
        </span>
        <ChevronDown size={20} className="ml-2 text-gray-900" />
    </button>
));

CustomDateInput.displayName = 'CustomDateInput';

const STATUS_OPTIONS = [
    { id: '', label: 'All Status' },
    { id: 'excess', label: 'Excess' },
    { id: 'warning', label: 'Warning' },
    { id: 'ok', label: 'OK' },
    { id: 'critical', label: 'Critical' }
];

const DashboardHeader = ({
    activeTab,
    setActiveTab,
    onLoading,
    projectName: controlledProjectName,
    onProjectChange: controlledOnProjectChange,
    date: controlledDate,
    onDateChange: controlledOnDateChange,
    projects: controlledProjects,
    criticalItemsCount = 0,
    onViewSample,
    categories = [],
    categoryFilter = '',
    statusFilter = '',
    onCategoryChange,
    onStatusChange
}) => {
    const [localProjectName, setLocalProjectName] = useState('Vedic Lifestyle');
    const [localDate, setLocalDate] = useState(new Date());
    const [isOpen, setIsOpen] = useState(false);
    const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
    const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);

    const isControlled = controlledProjectName !== undefined && controlledOnProjectChange !== undefined;
    const projectName = isControlled ? controlledProjectName : localProjectName;
    const date = isControlled && controlledDate !== undefined ? controlledDate : localDate;
    const projectList = isControlled && controlledProjects?.length ? controlledProjects.map((p) => p.label) : ['Vedic Lifestyle'];

    const handleProjectChange = (project) => {
        if (isControlled) {
            controlledOnProjectChange(project);
        } else {
            setLocalProjectName(project);
        }
        setIsOpen(false);
    };

    const handleDateChange = (date) => {
        if (isControlled && controlledOnDateChange) {
            controlledOnDateChange(date);
        } else {
            setLocalDate(date);
        }
    };

    useEffect(() => {
        if (!categoryDropdownOpen && !statusDropdownOpen) return;
        const handleClick = () => {
            setCategoryDropdownOpen(false);
            setStatusDropdownOpen(false);
        };
        window.addEventListener('click', handleClick);
        return () => window.removeEventListener('click', handleClick);
    }, [categoryDropdownOpen, statusDropdownOpen]);

    return (
        <div className="bg-[#EEF3F7] p-6  flex flex-col gap-4 z-50 sticky top-0">
            <div className="flex flex-col xl:flex-row  justify-between items-start xl:items-center gap-4">
                <div className="flex flex-col md:flex-row gap-4 items-start md:items-center w-full md:w-auto">
                    {/* Project Selector - Dropdown - z-[100] so it appears above Category/Status filters */}
                    <div className="relative z-[100] w-full md:w-auto">
                        <button
                            onClick={() => setIsOpen(!isOpen)}
                            className="flex items-center justify-between px-4 py-2 bg-white border border-gray-200 rounded-lg shadow-sm text-gray-900 font-bold text-base min-w-[240px] w-full md:w-auto hover:bg-gray-50 transition-colors"
                        >
                            <span>{projectName}</span>
                            <ChevronDown size={20} className={`ml-2 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                        </button>

                        {isOpen && (
                            <div className="absolute top-full left-0 mt-2 w-full bg-white border border-gray-100 rounded-lg shadow-xl py-1 overflow-hidden animate-in fade-in zoom-in-95 duration-100">
                                {projectList.map((project) => (
                                    <button
                                        key={project}
                                        className="w-full text-left px-4 py-2.5 hover:bg-gray-50 text-sm font-medium text-gray-700 flex justify-between items-center transition-colors"
                                        onClick={() => handleProjectChange(project)}
                                    >
                                        {project}
                                        {projectName === project && <Check size={16} className="text-[#1F4B55]" />}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Date Picker - z-[100] so it appears above Category/Status filters */}
                    <div className="relative z-[100] w-full md:w-auto">
                        <DatePicker
                            selected={date}
                            onChange={handleDateChange}
                            customInput={<CustomDateInput />}
                            dateFormat="MMMM yyyy"
                            showMonthYearPicker
                            maxDate={new Date(new Date().getFullYear(), new Date().getMonth(), 0)}
                            popperPlacement="bottom-start"
                            popperClassName="!z-[100]"
                        />
                    </div>
                </div>

                <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
                    {onViewSample && (
                        <button
                            type="button"
                            onClick={onViewSample}
                            className="flex items-center gap-2 px-4 py-2 bg-[#1F4B55] text-white rounded-md text-sm font-semibold hover:bg-[#163a42] transition-colors"
                        >
                            <FileText size={18} />
                            View Sample
                        </button>
                    )}
                    {/* Toggle Group */}
                    <div className="flex items-center bg-white p-1 rounded-lg border border-gray-100 shadow-sm">
                        {['Summary', 'Risk Overview'].map((tab) => {
                            const icons = {
                                Summary: LayoutGrid,
                                'Risk Overview': AlertTriangle
                            };
                            const Icon = icons[tab];
                            return (
                                <button
                                    key={tab}
                                    onClick={() => {
                                        setActiveTab(tab);
                                        // if (onLoading) onLoading();
                                    }}
                                    className={`flex items-center px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 ${activeTab === tab
                                        ? 'bg-gray-100 text-gray-900 shadow-sm'
                                        : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'
                                        }`}
                                >
                                    <Icon size={14} className="mr-1.5" />
                                    {tab}
                                </button>
                            );
                        })}
                    </div>

                    {/* Attention Button - count from items with stock_criticality < 100 (Critical) */}
                    {criticalItemsCount > 0 && (
                        <button className="flex items-center px-4 py-1.5 bg-white border border-red-200 rounded-full text-red-600 text-sm font-medium hover:bg-red-50 transition-colors shadow-sm">
                            {criticalItemsCount} Item{criticalItemsCount !== 1 ? 's' : ''} need attention
                        </button>
                    )}
                </div>
            </div>

            {/* Header Tabs */}
            {activeTab === 'Summary' && (
                <div className="flex flex-wrap items-center gap-4 mb-2">
                    {/* Progress Overview tab row */}
                    <div className="flex">
                        <div
                            className="bg-[#D4B95E] text-white px-10 py-3 font-medium text-base relative z-10 pr-16 shadow-md"
                            style={{ clipPath: 'polygon(0 0, 100% 0, 85% 100%, 0% 100%)' }}
                        >
                            Progress Overview
                        </div>
                        <div
                            className="bg-[#1E4C56] text-white px-10 py-3 font-medium text-base -ml-10 pl-16 relative z-0 pr-16 shadow-md"
                            style={{ clipPath: 'polygon(0 0, 100% 0, 85% 100%, 0% 100%)' }}
                        >
                            Minor Delays
                        </div>
                    </div>
                    {/* Filters in same row */}
                    {onCategoryChange && (
                        <div className="relative z-50 w-full md:w-auto">
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setCategoryDropdownOpen(!categoryDropdownOpen);
                                    setStatusDropdownOpen(false);
                                }}
                                className="flex items-center justify-between px-4 py-2 bg-white border border-gray-200 rounded-lg shadow-sm text-gray-900 font-bold text-base min-w-[200px] w-full md:w-auto hover:bg-gray-50 transition-colors"
                            >
                                <span>{categoryFilter ? (categories.find((c) => String(c.id) === String(categoryFilter))?.label || 'Category') : 'All Categories'}</span>
                                <ChevronDown size={20} className={`ml-2 text-gray-500 transition-transform ${categoryDropdownOpen ? 'rotate-180' : ''}`} />
                            </button>
                            {categoryDropdownOpen && (
                                <div className="absolute top-full left-0 mt-2 w-full bg-white border border-gray-100 rounded-lg shadow-xl py-1 overflow-y-auto max-h-60 animate-in fade-in zoom-in-95 duration-100">
                                    <button
                                        type="button"
                                        className="w-full text-left px-4 py-2.5 hover:bg-gray-50 text-sm font-medium text-gray-700 flex justify-between items-center"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onCategoryChange('');
                                            setCategoryDropdownOpen(false);
                                        }}
                                    >
                                        All Categories
                                        {!categoryFilter && <Check size={16} className="text-[#1F4B55]" />}
                                    </button>
                                    {categories.map((cat) => (
                                        <button
                                            key={cat.id}
                                            type="button"
                                            className="w-full text-left px-4 py-2.5 hover:bg-gray-50 text-sm font-medium text-gray-700 flex justify-between items-center"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onCategoryChange(String(cat.id));
                                                setCategoryDropdownOpen(false);
                                            }}
                                        >
                                            {cat.label}
                                            {String(categoryFilter) === String(cat.id) && <Check size={16} className="text-[#1F4B55]" />}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                    {onStatusChange && (
                        <div className="relative z-50 w-full md:w-auto">
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setStatusDropdownOpen(!statusDropdownOpen);
                                    setCategoryDropdownOpen(false);
                                }}
                                className="flex items-center justify-between px-4 py-2 bg-white border border-gray-200 rounded-lg shadow-sm text-gray-900 font-bold text-base min-w-[200px] w-full md:w-auto hover:bg-gray-50 transition-colors"
                            >
                                <span>{STATUS_OPTIONS.find((o) => o.id === statusFilter)?.label || 'All Status'}</span>
                                <ChevronDown size={20} className={`ml-2 text-gray-500 transition-transform ${statusDropdownOpen ? 'rotate-180' : ''}`} />
                            </button>
                            {statusDropdownOpen && (
                                <div className="absolute top-full left-0 mt-2 w-full bg-white border border-gray-100 rounded-lg shadow-xl py-1 overflow-hidden animate-in fade-in zoom-in-95 duration-100">
                                    {STATUS_OPTIONS.map((opt) => (
                                        <button
                                            key={opt.id}
                                            type="button"
                                            className="w-full text-left px-4 py-2.5 hover:bg-gray-50 text-sm font-medium text-gray-700 flex justify-between items-center"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onStatusChange(opt.id);
                                                setStatusDropdownOpen(false);
                                            }}
                                        >
                                            {opt.label}
                                            {statusFilter === opt.id && <Check size={16} className="text-[#1F4B55]" />}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {activeTab === 'Risk Overview' && (
                <div className="flex mb-2">
                    <div
                        className="bg-[#D4B95E] text-white px-10 py-3 font-medium text-base relative z-10 pr-16 shadow-md"
                        style={{ clipPath: 'polygon(0 0, 100% 0, 85% 100%, 0% 100%)' }}
                    >
                        Risk Overview
                    </div>
                    <div
                        className="bg-[#1E4C56] text-white px-10 py-3 font-medium text-base -ml-10 pl-16 relative z-0 pr-16 shadow-md"
                        style={{ clipPath: 'polygon(0 0, 100% 0, 85% 100%, 0% 100%)' }}
                    >
                        Detailed Analysis
                    </div>
                </div>
            )}


        </div>
    );
};

export default DashboardHeader;
